/**
 * One piece of a message, ready to render.
 *
 * A segment with an href is a link; a segment without one is plain text. The
 * text is always what the reader sees, so joining every segment's text back
 * together returns the original message character for character — which is how
 * a bubble that preserves newlines keeps looking exactly as it did before.
 */
export interface LinkSegment {
  text: string;
  href?: string;
}

/*
 * What ends a URL. Whitespace and the quote characters are here so a link
 * cannot swallow the rest of the line.
 *
 * The control characters and the Unicode bidi formatting characters are here
 * because neither survives being read. A control character in an href is
 * invisible to the reader and means nothing to anybody, and a bidi override
 * paints the rest of the link backwards, so the address a reader sees is not
 * the address the link goes to. Ending the match at those characters leaves
 * them in the plain text around it, where they change nothing and where the
 * link reads as the place it actually leads.
 */
const URL_STOPS =
  "\\s<>\"'\\u0000-\\u001F\\u007F\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069";

/*
 * What may become a link, in the order the alternatives are tried.
 *
 * The email address comes first so the host inside "someone@www.example.com"
 * is not linked a second time as a bare address of its own.
 *
 * The lengths written into the address are the ones the standards already
 * impose: 64 characters before the "@" and 63 in each label after it. They are
 * spelled out rather than left open because an unbounded run is what made the
 * search quadratic. With no ceiling the engine reads to the end of a long word
 * hunting for an "@", fails, and starts again one character along. Ordinary
 * text with no link in it at all was enough to do it: ten thousand characters
 * took 157ms, forty thousand took two and a half seconds, and the whole page
 * sat still for it. No address anybody can actually send to is lost to the
 * limits.
 *
 * Only http and https appear here. Every other scheme a browser understands —
 * javascript:, data:, vbscript:, file: — is deliberately absent, so a message
 * carrying one stays plain text. A support message is written by whoever is on
 * the other end of the conversation, and the shortest way to be sure none of
 * those ever reaches an href is to never match them in the first place.
 *
 * A bare host is only linked when it starts with "www.". Requiring that rather
 * than accepting any dotted word is what keeps "e.g." and "node.js" as the
 * words somebody typed instead of turning them into links to nowhere.
 */
const LINK_CANDIDATE_SOURCE = [
  "[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9-]{1,63}(?:\\.[A-Za-z0-9-]{1,63})*\\.[A-Za-z]{2,}",
  `https?://[^${URL_STOPS}]+`,
  `www\\.[^${URL_STOPS}]+`,
].join("|");

/*
 * The longest message the scan will look at.
 *
 * This is not what keeps the page responsive — the bounds in the address above
 * are, and a message this long now costs a couple of milliseconds. It is a
 * ceiling on the work any single message can ask for, whatever those patterns
 * grow into later. A message longer than this is rendered as the plain text it
 * always was, addresses in it included.
 */
const MAX_SCANNED_LENGTH = 10000;

/*
 * A link has to start on its own. Without this the "www.example.com" sitting
 * inside a longer run of characters — or inside an address the email branch
 * has already claimed — would be linked as though somebody had written it by
 * itself.
 *
 * Only the characters that genuinely carry a word on are listed. A full stop
 * is not one of them. Ending one sentence and opening the address on the same
 * line — "Here is the invite.https://meet.google.com/abc-defg-hij" — is an
 * ordinary thing for the scheduler to write, and counting the full stop as
 * part of the link left the address sitting there as plain text with nothing
 * to show anybody that something had been missed. The "@" stays, because it is
 * what stops the host inside an address being linked a second time, and the
 * "/" stays, because it is what turns down a scheme-relative "//www.evil.com".
 */
const JOINED_TO_PREVIOUS = /[A-Za-z0-9@/]/;

/*
 * The punctuation a sentence leaves on the end of a URL. A full stop after a
 * meeting link belongs to the sentence, not to the link, and a request for
 * "meet.google.com/abc-defg-hij." fails.
 *
 * The list runs past the ASCII marks because of where these messages come
 * from. An agent pasting out of Word or Outlook brings curly quotes and dashes
 * with them, and the scheduler writes emphasis the way WhatsApp does —
 * "*Join:* <url>*" — so a star, an underscore or a tilde arrives stuck to the
 * end of an address about as often as a full stop does. Left on, they go into
 * the href, and the reader presses a link that looks perfectly good and gets a
 * 404 back. That is worse than plain text, because it looks trustworthy.
 *
 * The hyphen is deliberately absent. Hyphens are part of the address rather
 * than punctuation around it — a Google Meet code is written "abc-defg-hij" —
 * so one on the end is left where it is.
 *
 * The danda (।) and the double danda (॥) are here for the same reason as the
 * full stop, and they matter more than the rest of this list put together:
 * these announcements are written in Bengali, where that is how a sentence
 * ends, and it is written hard against the word before it. Without it,
 * "নোটিশ https://smartcomm.com/notice। পড়ুন" sent the reader to an address
 * with a danda on the end, which 404s — a link that looks perfectly good and
 * is not.
 */
const TRAILING_PUNCTUATION = ".,;:!?'\"*_~…“”‘’«»—–।॥";

const CLOSING_TO_OPENING: Record<string, string> = {
  ")": "(",
  "]": "[",
  "}": "{",
};

/*
 * The only schemes allowed to reach an href, checked one last time on the
 * finished string. The patterns above cannot produce anything else today; this
 * is what keeps that true if they are ever widened, and it is also what turns
 * a scheme-relative "//host" away, since a browser would quietly resolve that
 * against whatever scheme the page itself was served over.
 */
const SAFE_HREF = /^(?:https?:\/\/|mailto:)/i;

function countOccurrences(text: string, character: string): number {
  let total = 0;
  for (const current of text) {
    if (current === character) total += 1;
  }
  return total;
}

/**
 * Give back the punctuation that belongs to the sentence rather than the link.
 *
 * Closing brackets are counted rather than simply dropped. A wikipedia address
 * ends in a bracket of its own — "/wiki/Foo_(bar)" — and cutting that off
 * leaves a link that no longer resolves, so a closing bracket is only removed
 * while there are more of them than there are opening ones to match.
 */
function trimTrailingPunctuation(candidate: string): string {
  let end = candidate.length;

  while (end > 0) {
    const character = candidate[end - 1];

    if (TRAILING_PUNCTUATION.includes(character)) {
      end -= 1;
      continue;
    }

    const opening = CLOSING_TO_OPENING[character];
    if (opening) {
      const sliced = candidate.slice(0, end);
      if (
        countOccurrences(sliced, character) > countOccurrences(sliced, opening)
      ) {
        end -= 1;
        continue;
      }
    }

    break;
  }

  return candidate.slice(0, end);
}

/**
 * Turn a matched candidate into an href, or into nothing at all.
 *
 * An address gets mailto: and a bare host gets https:, and whatever comes out
 * has to be http, https or mailto to be handed back. Anything else is refused,
 * and the caller renders it as the plain text it always was.
 */
function toSafeHref(candidate: string): string | undefined {
  const hasScheme = /^https?:\/\//i.test(candidate);

  let href = candidate;
  if (!hasScheme && candidate.includes("@")) {
    href = `mailto:${candidate}`;
  } else if (!hasScheme) {
    href = `https://${candidate}`;
  }

  return SAFE_HREF.test(href) ? href : undefined;
}

/**
 * Split a message into ordered segments of text and links.
 *
 * Deliberately returns pieces rather than a string of HTML. A support message
 * is written by somebody else, and building markup out of it — however well it
 * is escaped on the way — is what puts a page one mistake away from running a
 * stranger's script. Every segment here is rendered by Vue as a text node, so
 * there is no markup for anybody to smuggle anything into.
 *
 * An empty message gives back nothing to render, and a message with no links
 * gives back one plain segment. Whitespace is never touched: the segments
 * joined back together are the message that went in.
 */
export function splitIntoLinkSegments(text?: string | null): LinkSegment[] {
  const source = typeof text === "string" ? text : "";
  if (!source) return [];
  if (source.length > MAX_SCANNED_LENGTH) return [{ text: source }];

  // Built per call rather than shared. A global regex carries its own lastIndex
  // between calls, and one message could then start scanning halfway through.
  const pattern = new RegExp(LINK_CANDIDATE_SOURCE, "gi");

  const segments: LinkSegment[] = [];
  let plainFrom = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(source)) !== null) {
    const start = match.index;

    // A turned-down match is not stepped over, it is stepped into. Carrying on
    // from the end of it would take the search past everything inside it too,
    // and there can be a perfectly good address in there — the one worth
    // linking in "a/www.x.com/foo?u=https://y.com" is not the one just
    // refused. Starting again one character along costs nothing, because the
    // scan only ever moves forwards.
    const previous = start > 0 ? source[start - 1] : "";
    if (previous && JOINED_TO_PREVIOUS.test(previous)) {
      pattern.lastIndex = start + 1;
      continue;
    }

    const candidate = trimTrailingPunctuation(match[0]);
    if (!candidate) {
      pattern.lastIndex = start + 1;
      continue;
    }

    const href = toSafeHref(candidate);
    if (!href) {
      pattern.lastIndex = start + 1;
      continue;
    }

    if (start > plainFrom) {
      segments.push({ text: source.slice(plainFrom, start) });
    }

    segments.push({ text: candidate, href });

    // Carry on from the end of what was kept, so punctuation handed back to
    // the sentence is still there to be rendered.
    plainFrom = start + candidate.length;
    pattern.lastIndex = plainFrom;
  }

  if (plainFrom < source.length) {
    segments.push({ text: source.slice(plainFrom) });
  }

  return segments;
}
